<template>
  <div class="searchable-list-wrapper">
    <div class="search-input-group">
      <va-input
        v-model="searchTermInput"
        :label="label"
        :placeholder="placeholder"
        @keydown.enter="triggerSearch"
        class="search-input"
      />
      <va-button
        @click="triggerSearch"
        :loading="loading && !searchHasBeenPerformed"
        class="search-button"
      >
        Search
      </va-button>
    </div>

    <va-inner-loading
      :loading="loading && searchHasBeenPerformed"
      class="results-loading-indicator"
    >
      <div
        v-if="
          searchHasBeenPerformed && !loading && filteredOptions.length === 0
        "
        class="no-results"
      >
        {{ noOptionsTextToDisplay }}
      </div>
      <div
        v-if="filteredOptions.length > 0"
        class="results-scroll-area"
        ref="scrollAreaRef"
      >
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="result-item"
          :class="{ selected: option.value === props.modelValue }"
          @click="selectOption(option)"
          role="option"
          :aria-selected="option.value === props.modelValue"
        >
          {{ option.label }}
        </div>
      </div>
    </va-inner-loading>
    <div v-if="!searchHasBeenPerformed && !loading" class="initial-message">
      {{ noOptionsTextToDisplay }}
    </div>
    <!-- Display current selection outside the scrollable area -->
    <div
      v-if="props.modelValue && selectedOptionLabel && !loading"
      class="current-selection-display"
    >
      <strong>Selected {{ entityType }}:</strong> {{ selectedOptionLabel }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: null
  },
  label: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: "Enter search term..."
  },
  searchFunction: {
    type: Function,
    required: true
  },
  entityType: {
    type: String,
    default: "item" // Default to a singular noun
  }
});

const emit = defineEmits(["update:modelValue"]);

const searchTermInput = ref("");
const filteredOptions = ref([]);
const loading = ref(false);
const searchHasBeenPerformed = ref(false);
const scrollAreaRef = ref(null); // Reference for the scroll area if scroll manipulation is needed

const selectedOptionLabel = computed(() => {
  const selected = filteredOptions.value.find(
    opt => opt.value === props.modelValue
  );
  return selected ? selected.label : props.modelValue ? props.modelValue : null; // Display the ID if the label is not in the current options
});

watch(
  () => props.modelValue,
  async (newValue, oldValue) => {
    if (!newValue && oldValue) {
      searchTermInput.value = "";
      filteredOptions.value = [];
      searchHasBeenPerformed.value = false;
    }
  }
);

const triggerSearch = async () => {
  searchHasBeenPerformed.value = false;
  loading.value = true;
  if (!searchTermInput.value || searchTermInput.value.length < 3) {
    filteredOptions.value = [];
    loading.value = false;
    searchHasBeenPerformed.value = true;
    if (props.modelValue) {
      emit("update:modelValue", null);
    }
    return;
  }

  try {
    const result = await props.searchFunction(searchTermInput.value);
    filteredOptions.value = result || [];
    if (
      props.modelValue &&
      !filteredOptions.value.some(o => o.value === props.modelValue)
    ) {
      emit("update:modelValue", null);
    }
  } catch (error) {
    filteredOptions.value = [];
  } finally {
    loading.value = false;
    searchHasBeenPerformed.value = true;
  }
};

const selectOption = option => {
  emit("update:modelValue", option.value);
  // Optional: update search field with the selected option's label
  // searchTermInput.value = option.label;
  // Optional: clear results after selection to force a new search?
  // filteredOptions.value = [];
  // searchHasBeenPerformed.value = false;
};

const noOptionsTextToDisplay = computed(() => {
  // Note: loading is now handled by va-inner-loading for the results area
  if (!searchHasBeenPerformed.value && !loading.value) {
    return `Click search or press enter to find ${props.entityType}s`;
  }
  if (
    searchTermInput.value &&
    searchTermInput.value.length < 3 &&
    searchHasBeenPerformed.value &&
    !loading.value
  ) {
    return "Type at least 3 characters and search";
  }
  if (
    filteredOptions.value.length === 0 &&
    searchHasBeenPerformed.value &&
    !loading.value
  ) {
    return `No ${props.entityType}s found for "${searchTermInput.value}"`;
  }
  return ""; // Display nothing if options are present or loading
});
</script>

<style scoped>
.searchable-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative; /* For absolute positioning of the loading indicator if needed */
}
.search-input-group {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}
.search-input {
  flex-grow: 1;
}
.results-loading-indicator {
  /* If va-inner-loading doesn't wrap well, we can style it to have a minimum height */
  min-height: 50px; /* To make the spinner visible even if the scroll area is initially empty */
}
.results-scroll-area {
  max-height: 200px; /* Max height before scrollbar appears */
  overflow-y: auto;
  border: 1px solid var(--va-background-border, #ccc); /* Use a Vuestic variable or a fallback */
  border-radius: 4px;
  background-color: var(--va-background-primary, #fff);
}
.result-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--va-background-element, #eee);
}
.result-item:last-child {
  border-bottom: none;
}
.result-item:hover {
  background-color: var(--va-focus, #eee); /* Vuestic color for hover */
}
.result-item.selected {
  background-color: var(--va-primary, #2c82e0); /* Vuestic primary color */
  color: var(--va-on-primary, #fff); /* Text on primary color */
  font-weight: bold;
}
.no-results,
.initial-message {
  padding: 0.5rem 0.75rem;
  color: var(--va-text-secondary, #666);
  text-align: center;
}
.current-selection-display {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: var(--va-background-element, #f0f0f0);
  border-radius: 4px;
  font-size: 0.9em;
}
</style>
